import { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Lock, User, Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { AuthContext } from "../App";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      login(res.data.access_token);
      navigate("/admin/dashboard");
    } catch (e) {
      console.error("Login error", e);
      if (e.response?.status === 401) {
        setError("Ungültiger Benutzername oder Passwort");
      } else {
        setError("Ein Fehler ist aufgetreten. Bitte versuche es später erneut.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen gradient-hero flex flex-col">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-6">
        <nav className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="mobbing-melden.de Logo" 
              className="h-14 w-auto"
            />
          </Link>
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 md:px-12 lg:px-24 py-16">
        <div className="w-full max-w-md">
          <div className="card-base animate-fade-in-up" data-testid="admin-login-form">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#BDE0FE]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#1A365D]" strokeWidth={2} />
              </div>
              <h1 
                className="text-2xl md:text-3xl font-bold text-[#2D3748]"
                style={{ fontFamily: 'Nunito' }}
              >
                Admin-Bereich
              </h1>
              <p className="text-[#718096] mt-2">
                Melde dich an, um Fälle zu bearbeiten
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="flex items-center gap-2 p-4 bg-[#FC8181]/10 border border-[#FC8181]/20 rounded-xl text-[#FC8181]">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#2D3748] mb-2">
                  Benutzername
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="admin"
                    className="input-base w-full pl-12"
                    required
                    data-testid="username-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2D3748] mb-2">
                  Passwort
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input-base w-full pl-12 pr-12"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A0AEC0] hover:text-[#718096]"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading || !username || !password}
                className="btn-primary w-full"
                data-testid="login-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Anmelden...
                  </>
                ) : (
                  "Anmelden"
                )}
              </Button>
            </form>

          </div>
        </div>
      </main>
    </div>
  );
}
